import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

interface StockNote {
    id: number;
    symbole: string;
    content: string;
    snapshot_cours: number | null;
    created_at: string;
    updated_at: string;
}

interface StockTask {
    id: number;
    symbole: string;
    title: string;
    status: string;
    priority: string;
    due_date: string | null;
    created_at: string;
    completed_at: string | null;
}

interface StockNotesPanelProps {
    symbole: string;
    currentPrice?: number | null;
    isOpen: boolean;
    onClose: () => void;
}

const StockNotesPanel: React.FC<StockNotesPanelProps> = ({ symbole, currentPrice, isOpen, onClose }) => {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState<'notes' | 'tasks'>('notes');

    // Notes state
    const [notes, setNotes] = useState<StockNote[]>([]);
    const [newNote, setNewNote] = useState('');
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
    const [editingNoteContent, setEditingNoteContent] = useState('');

    // Tasks state
    const [tasks, setTasks] = useState<StockTask[]>([]);
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [newTaskPriority, setNewTaskPriority] = useState('MEDIUM');

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && token) {
            fetchNotes();
            fetchTasks();
        }
    }, [isOpen, symbole, token]);

    const fetchNotes = async () => {
        try {
            const response = await fetch(`${API_URL}/v1/notes/${symbole}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                setNotes(await response.json());
            }
        } catch (err) {
            console.error('Error fetching notes:', err);
        }
    };

    const fetchTasks = async () => {
        try {
            const response = await fetch(`${API_URL}/v1/tasks/${symbole}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                setTasks(await response.json());
            }
        } catch (err) {
            console.error('Error fetching tasks:', err);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/notes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ symbole, content: newNote })
            });
            if (response.ok) {
                setNewNote('');
                fetchNotes();
            }
        } catch (err) {
            console.error('Error adding note:', err);
        }
        setLoading(false);
    };

    const handleUpdateNote = async (noteId: number) => {
        if (!editingNoteContent.trim()) return;
        try {
            const response = await fetch(`${API_URL}/v1/notes/${noteId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ content: editingNoteContent })
            });
            if (response.ok) {
                setEditingNoteId(null);
                fetchNotes();
            }
        } catch (err) {
            console.error('Error updating note:', err);
        }
    };

    const handleDeleteNote = async (noteId: number) => {
        if (!window.confirm('Supprimer cette note ?')) return;
        try {
            await fetch(`${API_URL}/v1/notes/${noteId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchNotes();
        } catch (err) {
            console.error('Error deleting note:', err);
        }
    };

    const handleAddTask = async () => {
        if (!newTaskTitle.trim()) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/tasks`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    symbole,
                    title: newTaskTitle,
                    priority: newTaskPriority
                })
            });
            if (response.ok) {
                setNewTaskTitle('');
                fetchTasks();
            }
        } catch (err) {
            console.error('Error adding task:', err);
        }
        setLoading(false);
    };

    const handleChangeTaskStatus = async (task: StockTask, newStatus: string) => {
        if (newStatus === task.status) return;
        try {
            await fetch(`${API_URL}/v1/tasks/${task.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ status: newStatus })
            });
            fetchTasks();
        } catch (err) {
            console.error('Error updating task status:', err);
        }
    };

    const handleDeleteTask = async (taskId: number) => {
        if (!window.confirm('Supprimer cette tâche ?')) return;
        try {
            await fetch(`${API_URL}/v1/tasks/${taskId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchTasks();
        } catch (err) {
            console.error('Error deleting task:', err);
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'HIGH': return '#dc2626';
            case 'LOW': return '#6b7280';
            default: return '#f59e0b';
        }
    };

    const STATUS_OPTIONS: { value: string; label: string }[] = [
        { value: 'TODO', label: 'À faire' },
        { value: 'IN_PROGRESS', label: 'En cours' },
        { value: 'DONE', label: 'Terminée' },
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'DONE': return '#16a34a';
            case 'IN_PROGRESS': return '#f59e0b';
            default: return '#6b7280';
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            right: 0,
            top: 0,
            height: '100vh',
            width: '400px',
            background: 'var(--bg-box)',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: '#fff'
            }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>📝 {symbole}</h3>
                    {currentPrice && (
                        <span style={{ fontSize: '12px', opacity: 0.9 }}>
                            Cours: {currentPrice.toLocaleString('fr-FR')} FCFA
                        </span>
                    )}
                </div>
                <button
                    onClick={onClose}
                    style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: '#fff',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px'
                    }}
                >
                    ✕
                </button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)' }}>
                <button
                    onClick={() => setActiveTab('notes')}
                    style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        background: activeTab === 'notes' ? 'var(--bg-input)' : 'transparent',
                        borderBottom: activeTab === 'notes' ? '2px solid #667eea' : 'none',
                        cursor: 'pointer',
                        fontWeight: activeTab === 'notes' ? 600 : 400,
                        color: 'var(--text-main)'
                    }}
                >
                    📄 Notes ({notes.length})
                </button>
                <button
                    onClick={() => setActiveTab('tasks')}
                    style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        background: activeTab === 'tasks' ? 'var(--bg-input)' : 'transparent',
                        borderBottom: activeTab === 'tasks' ? '2px solid #667eea' : 'none',
                        cursor: 'pointer',
                        fontWeight: activeTab === 'tasks' ? 600 : 400,
                        color: 'var(--text-main)'
                    }}
                >
                    ✅ Tâches ({tasks.filter(t => t.status !== 'DONE').length})
                </button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                {activeTab === 'notes' ? (
                    <>
                        {/* Add note form */}
                        <div style={{ marginBottom: '16px' }}>
                            <textarea
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                placeholder="Ajouter une note..."
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-input)',
                                    color: 'var(--text-main)',
                                    minHeight: '80px',
                                    resize: 'vertical',
                                    fontFamily: 'inherit'
                                }}
                            />
                            <button
                                onClick={handleAddNote}
                                disabled={loading || !newNote.trim()}
                                style={{
                                    marginTop: '8px',
                                    padding: '8px 16px',
                                    background: '#667eea',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    opacity: loading || !newNote.trim() ? 0.5 : 1
                                }}
                            >
                                ➕ Ajouter
                            </button>
                        </div>

                        {/* Notes list */}
                        {notes.length === 0 ? (
                            <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>
                                Aucune note pour {symbole}
                            </p>
                        ) : (
                            notes.map(note => (
                                <div
                                    key={note.id}
                                    style={{
                                        background: 'var(--bg-input)',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        marginBottom: '12px',
                                        border: '1px solid var(--border-color)',
                                        color: 'var(--text-main)'
                                    }}
                                >
                                    {editingNoteId === note.id ? (
                                        <>
                                            <textarea
                                                value={editingNoteContent}
                                                onChange={(e) => setEditingNoteContent(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid var(--border-color)',
                                                    background: 'var(--bg-input)',
                                                    color: 'var(--text-main)',
                                                    minHeight: '60px'
                                                }}
                                            />
                                            <div style={{ marginTop: '8px' }}>
                                                <button
                                                    onClick={() => handleUpdateNote(note.id)}
                                                    style={{
                                                        padding: '4px 12px',
                                                        background: '#16a34a',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        marginRight: '8px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Sauvegarder
                                                </button>
                                                <button
                                                    onClick={() => setEditingNoteId(null)}
                                                    style={{
                                                        padding: '4px 12px',
                                                        background: '#6b7280',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Annuler
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{note.content}</p>
                                            <div style={{
                                                marginTop: '8px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                fontSize: '11px',
                                                color: '#6b7280'
                                            }}>
                                                <span>
                                                    {formatDate(note.created_at)}
                                                    {note.snapshot_cours && (
                                                        <> • Cours: {note.snapshot_cours.toLocaleString('fr-FR')} F</>
                                                    )}
                                                </span>
                                                <div>
                                                    <button
                                                        onClick={() => {
                                                            setEditingNoteId(note.id);
                                                            setEditingNoteContent(note.content);
                                                        }}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteNote(note.id)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))
                        )}
                    </>
                ) : (
                    <>
                        {/* Add task form */}
                        <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
                            <input
                                type="text"
                                value={newTaskTitle}
                                onChange={(e) => setNewTaskTitle(e.target.value)}
                                placeholder="Nouvelle tâche..."
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-input)',
                                    color: 'var(--text-main)'
                                }}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                            />
                            <select
                                value={newTaskPriority}
                                onChange={(e) => setNewTaskPriority(e.target.value)}
                                style={{
                                    padding: '10px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-input)',
                                    color: 'var(--text-main)'
                                }}
                            >
                                <option value="LOW">🟢 Basse</option>
                                <option value="MEDIUM">🟡 Moyenne</option>
                                <option value="HIGH">🔴 Haute</option>
                            </select>
                            <button
                                onClick={handleAddTask}
                                disabled={loading || !newTaskTitle.trim()}
                                style={{
                                    padding: '10px 16px',
                                    background: '#667eea',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                ➕
                            </button>
                        </div>

                        {/* Tasks list */}
                        {tasks.length === 0 ? (
                            <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>
                                Aucune tâche pour {symbole}
                            </p>
                        ) : (
                            tasks.map(task => (
                                <div
                                    key={task.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '12px',
                                        background: task.status === 'DONE' ? 'rgba(22, 163, 74, 0.1)' : 'var(--bg-input)',
                                        borderRadius: '8px',
                                        marginBottom: '8px',
                                        border: '1px solid var(--border-color)'
                                    }}
                                >
                                    <select
                                        value={task.status}
                                        onChange={(e) => handleChangeTaskStatus(task, e.target.value)}
                                        style={{
                                            marginRight: '12px',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            padding: '4px 6px',
                                            borderRadius: '999px',
                                            border: `1.5px solid ${getStatusColor(task.status)}`,
                                            color: getStatusColor(task.status),
                                            background: 'var(--bg-input)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {STATUS_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    <div style={{ flex: 1 }}>
                                        <span style={{
                                            textDecoration: task.status === 'DONE' ? 'line-through' : 'none',
                                            color: task.status === 'DONE' ? '#6b7280' : 'var(--text-heading)'
                                        }}>
                                            {task.title}
                                        </span>
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                background: getPriorityColor(task.priority),
                                                marginRight: '4px'
                                            }}></span>
                                            {task.priority}
                                            {task.due_date && <> • Échéance: {new Date(task.due_date).toLocaleDateString('fr-FR')}</>}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleDeleteTask(task.id)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '14px'
                                        }}
                                    >
                                        🗑️
                                    </button>
                                </div>
                            ))
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default StockNotesPanel;
